import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

type BannedCustomer = {
  id: string;
  email: string;
  phone: string;
  name: string;
  reason: string;
  bannedBy: string;
  bannedAt: number;
  storeId: string;
  notifiedAt: number | null;
  notifyError: string;
};

export function useBannedCustomers(search?: string, page = 1) {
  const params = new URLSearchParams({ page: String(page), per_page: "25" });
  if (search) params.set("search", search);

  return useQuery({
    queryKey: ["bannedCustomers", search, page],
    queryFn: () => fetchJson<{ items: BannedCustomer[]; total: number }>(`/api/banned-customers?${params}`),
  });
}

export function useCheckOrderBanned(email?: string, phone?: string) {
  return useQuery({
    queryKey: ["bannedCheck", email, phone],
    queryFn: async () => {
      const result = await fetchJson<Record<string, BannedCustomer>>("/api/banned-customers/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customers: [{ orderId: "check", email, phone }] }),
      });
      return result["check"] || null;
    },
    enabled: !!(email || phone),
  });
}

export function useCheckBannedBulk(customers: { orderId: string; email?: string; phone?: string }[]) {
  return useQuery({
    queryKey: ["bannedCheckBulk", customers.map((c) => c.orderId).join(",")],
    queryFn: () =>
      fetchJson<Record<string, BannedCustomer>>("/api/banned-customers/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customers }),
      }),
    enabled: customers.length > 0,
  });
}

export function useBanCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      phone: string;
      name: string;
      reason?: string;
      storeId?: string;
      notify?: boolean;
    }) =>
      fetchJson<BannedCustomer>("/api/banned-customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bannedCustomers"] });
      qc.invalidateQueries({ queryKey: ["bannedCheck"] });
      qc.invalidateQueries({ queryKey: ["bannedCheckBulk"] });
    },
  });
}

export function useNotifyBannedCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<BannedCustomer>(`/api/banned-customers/${id}/notify`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bannedCustomers"] });
    },
  });
}

export function useUnbanCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/banned-customers/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bannedCustomers"] });
      qc.invalidateQueries({ queryKey: ["bannedCheck"] });
      qc.invalidateQueries({ queryKey: ["bannedCheckBulk"] });
    },
  });
}

export function useUpdateBan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      fetchJson(`/api/banned-customers/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bannedCustomers"] });
    },
  });
}
